import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import classNames from 'classnames';
import { useGetDepositMethods } from 'react-easyrocket';
import { PaymentMethod } from 'src/types';
import { AnimatedOpacity } from 'src/animation/AnimatedOpacity';
import { PaymentMethods } from 'src/pages/AccountDeposit/components/Deposit/components/PaymentMethods';
import { DepositHeader } from './components/DepositHeader';
import { useModalContext } from 'src/hooks/useModalContext';
import '../../styles.css';
import { FiatBlock } from './components/FiatBlock';
import { CryptoBlock } from './components/CryptoBlock';
import { TabsDeposit } from './components/HeaderDeposit';
import { CryptoChillMethods } from './components/CryptoChillMethods';
import { useCryptochill } from './hooks/useCryptochill';

export interface BalanceFormatted {
  currency: string;
  currencySign: string;
  data: string;
  amount: string;
}
interface DepositProps {
  isModal?: boolean;
}

const Deposit = ({ isModal = false }: DepositProps) => {
  const depositRef = useRef<HTMLInputElement>(null);
  const [currentMethods, setCurrentMethods] = useState<'methods' | 'crypto'>('methods');
  const {
    selectedCryptoMethod,
    isLoadingCryptoMethods,
    isFetchingMissingAddress,
    cryptoMethods,
    handleCryptoMethodSelect
  } = useCryptochill();
  const location = useLocation();
  const { openedModalName } = useModalContext();
  const {
    data,
    sortedDepositMethods: sortedPaymentMethods,
    isLoading: isLoadingDepositMethods,
    isSuccess
  } = useGetDepositMethods();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();

  useEffect(() => {
    if (location?.state?.scrollToDeposit) {
      setTimeout(() => {
        depositRef?.current?.scrollIntoView({
          behavior: 'smooth'
        });
      });
    }
  }, [location?.state?.scrollToDeposit]);

  useEffect(() => {
    if (!isSuccess) return;
    if (paymentMethod?.type === 'coin') return;
    if (paymentMethod?._reactDepositMethodId === undefined) {
      setPaymentMethod(sortedPaymentMethods[0] as PaymentMethod);
    }
  }, [isSuccess, paymentMethod?.type]);

  const onSelectPaymentMethod = (value: PaymentMethod) => setPaymentMethod(value);

  return (
    <div className="flex flex-col gap-7">
      <AnimatedOpacity>
        <div
          className={classNames(
            'flex flex-col gap-3 768:rounded-[20px] pt-[10px] deposit-card overflow-x-hidden',
            openedModalName === 'DEPOSIT' && 'px-5'
          )}
          ref={depositRef}
          id="depositPromocode">
          <DepositHeader />
          <div className="flex flex-col">
            <TabsDeposit
              setPaymentMethod={setPaymentMethod}
              setCurrentMethods={setCurrentMethods}
              currentMethods={currentMethods}
            />
            {currentMethods === 'methods' ? (
              <PaymentMethods
                isLoading={isLoadingDepositMethods}
                methods={(sortedPaymentMethods || []) as PaymentMethod[]}
                onSelectPaymentMethod={onSelectPaymentMethod}
                selectedMethod={paymentMethod?._reactDepositMethodId}
              />
            ) : (
              <CryptoChillMethods
                selectedCryptoMethod={selectedCryptoMethod}
                isLoadingCryptoMethods={isLoadingCryptoMethods}
                cryptoMethods={cryptoMethods}
                handleCryptoMethodSelect={handleCryptoMethodSelect}
              />
            )}
          </div>
          {currentMethods === 'methods' && (
            <FiatBlock
              isLoadingDepositMethods={isLoadingDepositMethods}
              isSuccess={isSuccess}
              depositMethods={data}
              isModal={isModal}
              paymentMethod={paymentMethod}
            />
          )}
          {currentMethods === 'crypto' && (
            <CryptoBlock
              selectedCryptoMethod={selectedCryptoMethod}
              isLoadingCryptoMethods={isLoadingCryptoMethods}
              isFetchingMissingAddress={isFetchingMissingAddress}
            />
          )}
        </div>
      </AnimatedOpacity>
    </div>
  );
};

// eslint-disable-next-line no-restricted-exports
export default Deposit;
